import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Empty,
  Input,
  Select,
  Space,
  Spin,
  Tag,
  Tree,
  Typography,
  App,
} from "antd";
import type { DataNode } from "antd/es/tree";
import useDebounce from "../../../hooks/useDebounce";
import { getPrograms } from "../../../services/programs/programs.service";
import { ProgramEntity } from "../../../services/programs/programs.types";
import { getProgramTree } from "../../../services/content-tree/content-tree.service";
import { TreeNode } from "../../../services/content-tree/content-tree.types";
import NodeTitle from "./NodeTitle";
import PageHeader from "../../../components/PageHeader";
import {
  STATUS_TAG,
  collectExpandedKeysForMatches,
  computeDefaultExpandedKeys,
  computeStatusSummary,
  describeStatusCount,
} from "./utils";

const buildTreeData = (node: TreeNode, searchTerm: string): DataNode => ({
  key: node.key,
  title: <NodeTitle node={node} searchTerm={searchTerm} />,
  children: [
    ...(node.children ?? []).map((child) => buildTreeData(child, searchTerm)),
    // Placeholder leaf so a branch cut by the backend node cap doesn't
    // silently look complete.
    ...(node.children_truncated
      ? [
          {
            key: `${node.key}-truncated-marker`,
            title: (
              <Typography.Text type="secondary" italic>
                … rama recortada (el árbol supera el límite de nodos)
              </Typography.Text>
            ),
            selectable: false,
            isLeaf: true,
          },
        ]
      : []),
  ],
});

const ContentMap: React.FC = () => {
  const { notification } = App.useApp();
  const [programs, setPrograms] = useState<ProgramEntity[]>([]);
  const [selectedProgramId, setSelectedProgramId] = useState<
    number | undefined
  >(undefined);

  const [tree, setTree] = useState<TreeNode | null>(null);
  const [treeMeta, setTreeMeta] = useState<{
    totalNodes: number;
    truncated: boolean;
  } | null>(null);
  const [treeLoading, setTreeLoading] = useState(false);
  const [treeError, setTreeError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 400);

  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [autoExpandParent, setAutoExpandParent] = useState(true);

  useEffect(() => {
    const loadPrograms = async () => {
      try {
        const data = await getPrograms();
        setPrograms(data);
      } catch (error: any) {
        notification.error({
          message: "Error",
          description:
            error?.response?.data?.message ??
            "No se pudieron cargar los programas.",
        });
      }
    };
    loadPrograms();
  }, [notification]);

  // Tracks the most recently requested program so that a slow, stale
  // response can't clobber the tree for a program selected afterwards.
  const latestRequestIdRef = useRef(0);

  const fetchTree = useCallback(async (programId: number) => {
    const requestId = ++latestRequestIdRef.current;
    setTreeLoading(true);
    setTreeError(null);
    try {
      const { root, total_nodes, truncated } = await getProgramTree(programId);
      if (requestId !== latestRequestIdRef.current) return;
      setTree(root);
      setTreeMeta({ totalNodes: total_nodes, truncated });
      setExpandedKeys(computeDefaultExpandedKeys(root));
      setAutoExpandParent(true);
    } catch (error: any) {
      if (requestId !== latestRequestIdRef.current) return;
      const message =
        error?.response?.data?.message ??
        "No se pudo cargar el árbol de contenido.";
      setTree(null);
      setTreeMeta(null);
      setTreeError(message);
      notification.error({ message: "Error", description: message });
    } finally {
      if (requestId === latestRequestIdRef.current) setTreeLoading(false);
    }
  }, [notification]);

  useEffect(() => {
    if (selectedProgramId === undefined) {
      latestRequestIdRef.current += 1; // invalidate any in-flight request
      setTree(null);
      setTreeMeta(null);
      setTreeError(null);
      setTreeLoading(false);
      return;
    }
    fetchTree(selectedProgramId);
  }, [selectedProgramId, fetchTree]);

  // Auto-expand ancestors of matches while searching; revert to the default
  // program/course-level expansion once the search is cleared.
  useEffect(() => {
    if (!tree) return;
    if (debouncedSearch.trim() === "") {
      setExpandedKeys(computeDefaultExpandedKeys(tree));
    } else {
      setExpandedKeys(collectExpandedKeysForMatches(tree, debouncedSearch));
    }
    setAutoExpandParent(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, tree]);

  const handleExpand = (keys: React.Key[]) => {
    setExpandedKeys(keys);
    setAutoExpandParent(false);
  };

  const treeData = useMemo<DataNode[]>(
    () => (tree ? [buildTreeData(tree, debouncedSearch)] : []),
    [tree, debouncedSearch]
  );

  const statusSummary = useMemo(
    () => (tree ? computeStatusSummary(tree) : []),
    [tree]
  );

  const programOptions = useMemo(
    () =>
      programs.map((program) => ({
        value: program.id,
        label: `${program.id} - ${program.title}`,
      })),
    [programs]
  );

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <PageHeader title="Mapa de contenido" />

      <Space wrap align="start">
        <Select
          showSearch
          allowClear
          placeholder="Selecciona un programa"
          style={{ width: 320 }}
          value={selectedProgramId}
          onChange={(value) => setSelectedProgramId(value)}
          options={programOptions}
          filterOption={(input, option) =>
            String(option?.label ?? "")
              .toLowerCase()
              .includes(input.toLowerCase())
          }
        />

        <Input
          allowClear
          placeholder="Buscar por ID o nombre..."
          style={{ width: 260 }}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />

        <Space size={4} wrap>
          {(Object.keys(STATUS_TAG) as Array<keyof typeof STATUS_TAG>).map(
            (status) => (
              <Tag key={status} color={STATUS_TAG[status].color}>
                {STATUS_TAG[status].label}
              </Tag>
            )
          )}
        </Space>
      </Space>

      {statusSummary.length > 0 && (
        <Space wrap>
          {statusSummary.map((entry) => (
            <Tag
              key={`${entry.type}-${entry.status}`}
              color={STATUS_TAG[entry.status].color}
            >
              {describeStatusCount(entry.type, entry.status, entry.count)}
            </Tag>
          ))}
        </Space>
      )}

      {treeMeta?.truncated && (
        <Alert
          type="warning"
          showIcon
          message="Árbol demasiado grande: se muestra una parte."
          description={`Este programa acumula ${treeMeta.totalNodes.toLocaleString(
            "es-MX"
          )} nodos (incluye contenido eliminado). Las ramas recortadas aparecen marcadas dentro del árbol; usá la búsqueda por ID para ubicar contenido puntual.`}
        />
      )}

      {treeError && (
        <Alert
          type="error"
          showIcon
          message="No se pudo cargar el árbol de contenido."
          description={treeError}
          action={
            <Button
              size="small"
              onClick={() =>
                selectedProgramId !== undefined && fetchTree(selectedProgramId)
              }
            >
              Reintentar
            </Button>
          }
        />
      )}

      <div style={{ minHeight: 200 }}>
        {treeLoading && (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin size="large" />
          </div>
        )}

        {!treeLoading && selectedProgramId === undefined && (
          <Empty description="Elegí un programa para ver su árbol de contenido." />
        )}

        {!treeLoading && tree && (
          <Tree
            treeData={treeData}
            expandedKeys={expandedKeys}
            autoExpandParent={autoExpandParent}
            onExpand={handleExpand}
            virtual={false}
            blockNode
          />
        )}
      </div>
    </Space>
  );
};

export default ContentMap;
